import React from 'react';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { ProductGrid } from '../components/ProductGrid';
import { LogisticsCalculator } from '../components/LogisticsCalculator';
import { PaymentMethods } from '../components/PaymentMethods';
import { CartSidebar } from '../components/CartSidebar';
import { Footer } from '../components/Footer';
import { CartItem, Page } from '../App';
import { motion } from 'framer-motion';
import { Gem, Home, Cpu, ArrowRight, ShieldCheck, Truck, Star } from 'lucide-react';
interface HomePageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
const categories = [{
  id: 'fashion',
  page: 'fashion' as Page,
  label: 'Fashion Accessories',
  description: 'Handbags, jewellery, scarves & more',
  icon: Gem,
  color: '#D4AF37',
  image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=800'
}, {
  id: 'appliances',
  page: 'appliances' as Page,
  label: 'Home Appliances',
  description: 'Smart TVs, blenders, air fryers & more',
  icon: Home,
  color: '#00FF87',
  image: 'https://images.unsplash.com/photo-1570222094114-28a9d88a2d64?auto=format&fit=crop&q=80&w=800'
}, {
  id: 'electronics',
  page: 'electronics' as Page,
  label: 'Electronics',
  description: 'Smartphones, earbuds & gadgets',
  icon: Cpu,
  color: '#60a5fa',
  image: 'https://cdn.magicpatterns.com/uploads/8cBVP6dQJouJukeTEWCVEZ/Redmi_Note_15_Pro.jpg'
}];
const trustBadges = [{
  icon: ShieldCheck,
  label: 'Paystack Secured',
  sub: '256-bit encryption'
}, {
  icon: Truck,
  label: 'Nigeria-Only Delivery',
  sub: 'Benin City base'
}, {
  icon: Star,
  label: 'NDPR Compliant',
  sub: 'Your data is safe'
}];
export function HomePage({
  onAddToCart,
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: HomePageProps) {
  return <div className="bg-[#0F0F0F] min-h-screen text-white selection:bg-[#D4AF37] selection:text-black" data-id="element-556">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-557" />

      <main data-id="element-558">
        <HeroSection onNavigate={onNavigate} data-id="element-559" />

        {/* Trust Badges */}
        <section className="py-8 bg-[#0a0a0a] border-y border-white/5" data-id="element-560">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-561">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-562">
              {trustBadges.map((badge, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center gap-4 justify-center sm:justify-start" data-id="element-563">
                  <div className="p-2 rounded-lg bg-[#D4AF37]/10" data-id="element-564">
                    <badge.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-565" />
                  </div>
                  <div data-id="element-566">
                    <p className="text-white text-sm font-semibold" data-id="element-567">
                      {badge.label}
                    </p>
                    <p className="text-gray-500 text-xs" data-id="element-568">{badge.sub}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Category Cards */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-569">
          <div className="text-center mb-12" data-id="element-570">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-3" data-id="element-571">
              Shop by <span className="text-[#D4AF37]" data-id="element-572">Category</span>
            </h2>
            <p className="text-gray-400" data-id="element-573">Explore our curated collections</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-574">
            {categories.map((cat, i) => <motion.button key={cat.id} onClick={() => onNavigate(cat.page)} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative rounded-2xl overflow-hidden h-64 text-left" data-id="element-575">
                <img src={cat.image} alt={cat.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-576" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-577" />
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-578">
                  <div className="flex items-center gap-2 mb-1" data-id="element-579">
                    <cat.icon className="w-4 h-4" style={{
                  color: cat.color
                }} data-id="element-580" />
                    <span className="text-xs uppercase tracking-wider font-medium" style={{
                  color: cat.color
                }} data-id="element-581">
                      {cat.label}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm mb-3" data-id="element-582">
                    {cat.description}
                  </p>
                  <div className="flex items-center gap-1 text-white text-sm font-semibold group-hover:text-[#D4AF37] transition-colors" data-id="element-583">
                    Shop Now{' '}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-584" />
                  </div>
                </div>
              </motion.button>)}
          </div>
        </section>

        <ProductGrid onAddToCart={onAddToCart} onNavigate={onNavigate} data-id="element-585" />
        <LogisticsCalculator data-id="element-586" />
        <PaymentMethods data-id="element-587" />
      </main>

      <Footer onNavigate={onNavigate} data-id="element-588" />

      <CartSidebar isOpen={isCartOpen} onClose={onCloseCart} items={cartItems} onUpdateQuantity={onUpdateQuantity} onRemoveItem={onRemoveItem} onNavigate={onNavigate} data-id="element-589" />
    </div>;
}